import {Component, OnInit, ViewChild} from '@angular/core';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {HealthCentersRoutesEnum} from '../services/navigation/health-centers-routes.enum';
import {ApiHealthCenterResponse} from '../services/http/api-health-center-response';
import {HealthCenterRestApiService} from '../services/http/health-center-rest-api.service';
import {MatSort, SortDirection} from '@angular/material/sort';
import {catchError, debounce, Observable, Subject, switchMap, takeUntil, timer, of} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiHealthCentersListResponse} from '../services/http/api-health-centers-list-response';
import {ApiHealthCentersListPaginatedQueryParams}
  from '../services/http/api-health-centers-list-paginated-query-params';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {Utils} from '@astek/utils';
import {PageEvent} from '@angular/material/paginator';
import {ApiRefreshTimerEnum} from '../../../core/services/http/api-refresh-timer.enum';

@Component({
  selector: 'xp-health-centers-list',
  templateUrl: './health-centers-list.component.html',
  styleUrls: ['../../crud.common.scss', './health-centers-list.component.scss'],
})
export class HealthCentersListComponent implements OnInit {

  public isLoading: boolean = true;

  public healthCenters: Array<ApiHealthCenterResponse> | null = null;

  public itemsCount: number = 0;
  public pageIndex: number = 0;
  public pageSize: number = 10;

  public displayedColumns: Array<string> = [
    'name',
    'address',
    'zipCode',
    'city',
    'country',
    'contactFirstName',
    'contactLastName',
    'email',
    'phoneNumber',
  ];

  public defaultSortOn: string = 'name';
  public defaultSortOrder: SortDirection = 'asc';
  @ViewChild(MatSort)
  public sort: MatSort = new MatSort();

  private _requestApiOnChangeSubject: Subject<number> = new Subject<number>();

  constructor(
    private readonly _healthCenterRestApiService: HealthCenterRestApiService,
    private readonly _snackBar: MatSnackBar,
    private readonly _translocoService: TranslocoService,
  ) {
    this._requestApiOnChangeSubject.pipe(
      takeUntilDestroyed(),
      debounce((dueTime: number) => timer(dueTime)),
      switchMap(() => this.requestHealthCenters().pipe(
        takeUntil(this._requestApiOnChangeSubject),
        catchError(() => {
          this.handleError();
          return of(null);
        }),
      )),
    ).subscribe({
      next: (response: ApiHealthCentersListResponse | null) => {
        if (Utils.isNotNullOrUndefined(response)) {
          this.handleRequestBoxesResponse(response);
        }
      },
    });
    this.displayedColumns.push('actions');
  }

  public get createHealthCenterRoute(): Array<string> {
    return ['/', RoutesEnum.HEALTH_CENTERS, HealthCentersRoutesEnum.CREATE];
  }

  public ngOnInit(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public clearSearch(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public onDateChange(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onSelectionChange(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public resetFilters(): void {
    this.clearSearch();
  }

  public onSortChange(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onPaginatorChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public generateViewHealthCenterRoute(id: string): string {
    return '/' + RoutesEnum.HEALTH_CENTERS + '/' + HealthCentersRoutesEnum.VIEW.replace(':healthCenterId', id);
  }

  public generateEditHealthCenterRoute(id: string): string {
    return '/' + RoutesEnum.HEALTH_CENTERS + '/' + HealthCentersRoutesEnum.EDIT.replace(':healthCenterId', id);
  }

  private requestHealthCenters(): Observable<ApiHealthCentersListResponse> {
    this.isLoading = true;
    const queryParams: ApiHealthCentersListPaginatedQueryParams = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sort: this.sort?.active ?? this.defaultSortOn,
      order: this.sort?.direction ?? this.defaultSortOrder,
    };
    return this._healthCenterRestApiService.requestHealthCenterListPaginated(queryParams);
  }

  private handleRequestBoxesResponse(response: ApiHealthCentersListResponse): void {
    this.isLoading = false;
    this.itemsCount = response.totalCount;
    this.healthCenters = response.healthCenters;
  }

  private handleError(): void {
    this.isLoading = false;
    this._snackBar.open(
      this._translocoService.translate('SHARED.ERRORS.UNKNOWN'),
      this._translocoService.translate('SHARED.CLOSE'),
    );
  }
}
